import { Link, useParams } from "react-router-dom";
import * as Lucide from "lucide-react";
import { Seo } from "@/components/Seo";
import { SectionHead, CtaBand, Breadcrumbs, LinkGrid } from "@/components/Shared";
import { SECTORS, SERVICES, CITIES } from "@/data/site";
import { url, metaSectors, metaSector, sectorBySlug, breadcrumbLd } from "@/data/content";
import NotFound from "@/pages/NotFound";

const Icon = ({ name, className }) => {
  const C = Lucide[name] || Lucide.Shield;
  return <C className={className} />;
};

const SECTOR_SERVICES = {
  "construction-sites": ["construction-site-security", "fire-watch-services", "mobile-patrol-security", "alarm-response"],
  "retail-commercial": ["retail-loss-prevention", "uniformed-security-guards", "mobile-patrol-security", "alarm-response"],
  "healthcare-facilities": ["healthcare-security", "uniformed-security-guards", "concierge-security", "alarm-response"],
  "schools-campuses": ["uniformed-security-guards", "event-security", "mobile-patrol-security", "concierge-security"],
  "industrial-manufacturing": ["industrial-security", "uniformed-security-guards", "mobile-patrol-security", "k9-security"],
  "hospitality-tourism": ["concierge-security", "event-security", "uniformed-security-guards", "executive-protection"],
  "government-municipal": ["uniformed-security-guards", "concierge-security", "event-security", "mobile-patrol-security"],
  "residential-communities": ["concierge-security", "mobile-patrol-security", "uniformed-security-guards", "alarm-response"],
  "transportation-logistics": ["airport-transit-security", "industrial-security", "mobile-patrol-security", "k9-security"],
  "energy-mining": ["industrial-security", "mobile-patrol-security", "fire-watch-services", "k9-security"],
};

export function SectorsIndex() {
  const meta = metaSectors();
  return (
    <div data-testid="sectors-index-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14">
      <Seo title={meta.title} description={meta.description} path="/sectors/" jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Sectors", path: "/sectors/" }])]} />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Sectors", path: "/sectors/" }]} />
      <SectionHead as="h1" eyebrow="Industries We Protect" title="Security programs engineered for your sector" desc="Ten industry verticals, each with its own risk profile, compliance demands and post orders." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {SECTORS.map((x) => (
          <Link key={x.slug} to={url.sector(x)} data-testid={`sector-card-${x.slug}`} className="group rounded-2xl border border-slate-800 bg-[#111827] p-7 transition-all hover:border-amber-500/50 hover:-translate-y-1">
            <Icon name={x.icon} className="h-7 w-7 text-amber-400 mb-4" />
            <h2 className="font-display font-bold text-lg text-slate-100 mb-2 group-hover:text-amber-400 transition-colors">{x.name}</h2>
            <p className="text-sm text-slate-400 leading-relaxed">{x.desc}</p>
          </Link>
        ))}
      </div>
      <CtaBand heading="Your Industry, Your Security Program" sub="Tell us your sector and site profile, and we will design the post orders around it. Same-day written quotes." />
    </div>
  );
}

export function SectorPage() {
  const { sectorSlug } = useParams();
  const x = sectorBySlug(sectorSlug);
  if (!x) return <NotFound />;
  const meta = metaSector(x);
  const related = (SECTOR_SERVICES[x.slug] || []).map((sl) => SERVICES.find((s) => s.slug === sl)).filter(Boolean);
  const topCities = CITIES.slice(0, 16);
  const crumbs = [{ name: "Home", path: "/" }, { name: "Sectors", path: "/sectors/" }, { name: x.name, path: url.sector(x) }];
  return (
    <div data-testid={`sector-page-${x.slug}`}>
      <Seo title={meta.title} description={meta.description} path={url.sector(x)} jsonLd={[breadcrumbLd(crumbs)]} />
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(245,158,11,0.12) 0%, transparent 65%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14 relative">
          <Breadcrumbs items={crumbs} />
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-amber-500/10 border border-amber-500/40 flex items-center justify-center">
              <Icon name={x.icon} className="h-7 w-7 text-amber-400" />
            </div>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500">Sector Security Program</span>
          </div>
          <h1 data-testid="sector-h1" className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-extrabold font-display tracking-tight text-slate-50 max-w-4xl">{x.name} Security Services in Canada</h1>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed max-w-2xl">{x.desc}</p>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="grid sm:grid-cols-3 gap-5 mb-14">
          {x.points.map((pt) => (
            <div key={pt} className="rounded-xl border border-slate-800 bg-[#111827] p-6">
              <Lucide.CheckCircle2 className="h-5 w-5 text-amber-400 mb-3" />
              <p className="text-sm text-slate-300 leading-relaxed">{pt}</p>
            </div>
          ))}
        </div>
        <LinkGrid testid="sector-services" title={`Recommended Services for ${x.name}`} links={related.map((s) => ({ path: url.service(s), label: s.name }))} />
        <LinkGrid testid="sector-cities" title={`${x.name} Security by City`} links={topCities.map((c) => ({ path: url.city(c), label: `${x.name} Security ${c.name}` }))} />
        <CtaBand heading={`Protect Your ${x.name} Operation`} />
      </div>
    </div>
  );
}
